import React, { useMemo } from 'react';
import { FormikValues } from 'formik';

import { ITicketTemplateItem } from '../../../services/ticketTemplates/types';
import { InputType } from '../../../constants/ticketTemplates';
import { RadioItemDefaultKey } from '../../../constants/strings';

import Input from '../../../components/Input/Input';
import Checkbox from '../../../components/Checkbox';
import CheckboxGroup from '../../../components/CheckboxGroup';
import RadioGroupComponent from '../../../components/RadioGroup';
import RewestIframe from './RewestIframe';

import useStyles from '../styles';

interface IProps extends ITicketTemplateItem {
  disabled: boolean;
  formik: FormikValues;
}

const TicketTemplateItem: React.FC<IProps> = ({
  title,
  inputType,
  id,
  isRequired,
  disabled,
  formik,
  values,
}) => {
  const classes = useStyles();

  const content = useMemo(() => {
    switch (inputType) {
      case InputType.Label:
        return <div />;
      case InputType.Input:
        return (
          <Input
            name={`questions.${id}`}
            formik={formik}
            disabled={disabled}
            multiline
            maxRows={5}
            minRows={1}
          />
        );
      case InputType.Checkbox:
        return (
          <Checkbox
            formik={formik}
            label={title}
            name={`questions.${id}`}
            defaultValue={false}
            disabled={disabled}
          />
        );
      case InputType.MultiSelect:
        return (
          <CheckboxGroup
            onChange={values => {
              formik.setFieldValue(
                `questions.${id}`,
                values.map(i => i.label),
              );
            }}
            options={values?.map(i => ({ label: i, value: i })) ?? []}
          />
        );
      case InputType.RadioGroup:
        return (
          <RadioGroupComponent
            onChange={value => {
              formik.setFieldValue(`questions.${id}`, value?.label);
            }}
            options={
              values?.map(i => ({
                label: i.replace(RadioItemDefaultKey, ''),
                value: i.replace(RadioItemDefaultKey, ''),
                default: i.startsWith(RadioItemDefaultKey),
              })) ?? []
            }
            disabled={disabled}
          />
        );
      case InputType.Rewest: {
        const rewestUrl = values && values.length > 0 ? values[0] : '';
        return <RewestIframe url={rewestUrl} title={title} />;
      }
      default:
        return undefined;
    }
  }, [disabled, inputType, formik, id, values]);

  const isValidUrl = (url: string): boolean => {
    try {
      const urlObject = new URL(url);
      return urlObject.protocol === 'http:' || urlObject.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const renderTitle = (): JSX.Element => {
    const isLink = isValidUrl(title) && inputType === InputType.Question;
    return (
      <b className={classes.title}>
        {isLink ? (
          <a href={title} target="_blank" rel="noopener noreferrer">
            {title}
          </a>
        ) : (
          title
        )}
      </b>
    );
  };

  return (
    <div className={classes.itemContainer}>
      {inputType !== InputType.Checkbox && (
        <div className={classes.itemHeader}>
          <span>{renderTitle()}</span>
          <span className={classes.itemHeaderStatus}>{isRequired ? '*' : ''}</span>
        </div>
      )}
      <div>{content}</div>
    </div>
  );
};

export default TicketTemplateItem;
